import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  updateStep,
  setCurrentStep,
  selectStep2Data,
  selectCurrentStep,
  selectIsOrganizationCreated,
  createOrganization,
  updateOrganization,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import { selectEffectiveUserInfo } from "@/store/redux/slices/auth/auth_slice";
import {
  uploadFile,
  selectFileUploadLoading,
} from "@/store/redux/slices/user/file_upload_slice";
import { getUserId, getOrganizationId } from "@/utils/token_functions";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import FileInput from "@/components/common/inputs/File_Input";
import CountryFlag from "@/components/common/Country_Flag";
import Success from "@/components/common/alerts/Success";
import { Switch, ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import ProfilePicker from "@/components/specific/Profile_Picker";
import { selectStep1Data } from "@/store/redux/slices/kyc/kyc_setup_slice";

import WebIcon from "@/components/common/Web_Icons";

const AccountSetupStep2 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from both slices
  const step2Data = useSelector(selectStep2Data);
  const currentStep = useSelector(selectCurrentStep);
  const userInfo = useSelector(selectEffectiveUserInfo);
  const isFileUploading = useSelector(selectFileUploadLoading);
  const isOrganizationCreated = useSelector(selectIsOrganizationCreated);

  // Add step1Data to get the profile image
  const step1Data = useSelector(selectStep1Data);

  const [profilePreview, setProfilePreview] = useState(null);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isSecondSuccessVisible, setIsSecondSuccessVisible] = useState(false);
  const [fullName, setFullName] = useState(
    step2Data?.ownerFullName || userInfo?.fullname || ""
  );
  const [email, setEmail] = useState(
    step2Data?.ownerEmail || userInfo?.email || ""
  );
  const [phone, setPhone] = useState(
    step2Data?.ownerPhone || userInfo?.phone || ""
  );
  const [isOwner, setIsOwner] = useState(step2Data?.isOwner || false);
  const [identityType, setIdentityType] = useState(
    step2Data?.modeOfIdentification || ""
  );
  const [identityDocument, setIdentityDocument] = useState(
    step2Data?.identificationImage || null
  );
  const [profileImage, setProfileImage] = useState(null);

  const isFormFilled = Boolean(
    fullName && email && phone && identityType && identityDocument
  );

  useEffect(() => {
    if (currentStep > 2) {
      setFullName(step2Data.ownerFullName || "");
      setEmail(step2Data.ownerEmail || "");
      setPhone(step2Data.ownerPhone || "");
      setIsOwner(step2Data.isOwner || false);
      setIdentityType(step2Data.modeOfIdentification || "");
    }

    const storedFileData = sessionStorage.getItem("identityDocumentData");
    if (storedFileData) {
      const { name, type, data } = JSON.parse(storedFileData);
      const file = new File(
        [Uint8Array.from(atob(data), (c) => c.charCodeAt(0))],
        name,
        { type }
      );
      setIdentityDocument(file);
    }
  }, [currentStep, step2Data]);

  // When arriving from settings, profile may load after mount; fill empty fields only.
  useEffect(() => {
    if (step2Data?.ownerFullName) return;
    if (!userInfo?.fullname && !userInfo?.email) return;

    setFullName((prev) => prev || userInfo.fullname || "");
    setEmail((prev) => prev || userInfo.email || "");
    setPhone((prev) => prev || userInfo.phone || "");
  }, [userInfo, step2Data?.ownerFullName]);

  const handleToggleChange = (checked) => {
    setIsOwner(checked);
    if (checked) {
      // Only update form fields, not profile image (auth + profile via selectEffectiveUserInfo)
      setFullName(userInfo?.fullname || "");
      setEmail(userInfo?.email || "");
      setPhone(userInfo?.phone || "");
    } else {
      // Only clear form fields, leave profile image unchanged
      setFullName("");
      setEmail("");
      setPhone("");
    }
  };

  useEffect(() => {
    if (step1Data?.ownerProfileImage) {
      setProfileImage(step1Data.ownerProfileImage);
      setProfilePreview(step1Data.ownerProfileImage);
    }
  }, [step1Data]);

  // Handle profile image separately
  const handleProfileImageChange = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          setProfilePreview(base64String);
          setProfileImage(base64String);

          // Store in Redux
          dispatch(
            updateStep({
              step: "step2",
              data: {
                ownerProfileImage: base64String,
              },
            })
          );
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
        setProfilePreview(null);
      }
    }
  };

  const handleIdentityDocumentChange = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64String = reader.result;
          setIdentityDocument(base64String);

          // Store in Redux
          dispatch(
            updateStep({
              step: "step2",
              data: {
                identificationImage: base64String,
              },
            })
          );

          await dispatch(
            uploadFile({
              file,
              fileType: "identificationImage",
            })
          );
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
      }
    }
  };

  const identityTypeOptions = [
    { value: "passport", label: "International Passport" },
    { value: "voter_card", label: "Permanent Voter Card" },
    { value: "electricity_bill", label: "Electricity Bill" },
    { value: "vocational_cert", label: "Recognized Vocational Certificate" },
  ];

  const handleProceed = async () => {
    showLoader();
    try {
      const userID = getUserId();

      // Store data in Redux
      await dispatch(
        updateStep({
          step: "step2",
          data: {
            ownerFullName: fullName,
            ownerEmail: email,
            ownerPhone: phone,
            isOwner,
            ownerID: isOwner ? userID : null,
            modeOfIdentification: identityType,
            identificationImage:
              identityDocument instanceof File
                ? identityDocument // Store base64 for preview
                : identityDocument, // Store URL if already uploaded
          },
        })
      );
      setIsSuccessVisible(true);
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleFirstSuccessProceed = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      dispatch(setCurrentStep(3));
      navigate("/account-setup-step-3");
    }, 2000);
  };

  const handleSaveAndProceedLater = async () => {
    setIsSuccessVisible(false);
    showLoader();

    try {
      const userID = getUserId();
      let filesToUpload = [];

      // Collect all files that need to be uploaded
      if (step1Data.ownerProfileImage instanceof File) {
        filesToUpload.push(step1Data.ownerProfileImage);
      }

      if (profileImage instanceof File) {
        filesToUpload.push(profileImage);
      }

      if (identityDocument instanceof File) {
        filesToUpload.push(identityDocument);
      }

      // Upload all files in a single request if there are any
      let uploadedFiles = [];
      if (filesToUpload.length > 0) {
        const uploadResult = await dispatch(
          uploadFile({
            file: filesToUpload,
          })
        ).unwrap();

        uploadedFiles = uploadResult;
      }

      // Map uploaded files to their respective fields
      const organizationData = {
        name: step1Data.name,
        type: step1Data.type,
        address: step1Data.address,
        email: step1Data.email,
        phone: step1Data.phone,
        ownerProfileImage:
          uploadedFiles[0]?.fileUrl || step1Data.ownerProfileImage,

        ownerFullName: fullName,
        ownerEmail: email,
        ownerPhone: phone,
        isOwner,
        modeOfIdentification: identityType,
        identificationImage: uploadedFiles[1]?.fileUrl || null,

        // Set userID as ownerID only if isOwner is true
        ownerID: isOwner ? userID : null,
      };

      // Use appropriate action based on organization creation status
      const shouldUpdate =
        isOrganizationCreated || Boolean(getOrganizationId());
      if (!shouldUpdate) {
        await dispatch(createOrganization(organizationData));
      } else {
        await dispatch(updateOrganization(organizationData));
      }

      setIsSecondSuccessVisible(true);
    } catch (error) {
      console.error("Error with organization:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSecondSuccess = () => {
    setIsSecondSuccessVisible(false);
  };

  const handleContinue = () => {
    setIsSecondSuccessVisible(false);
    dispatch(setCurrentStep(3));
    navigate("/account-setup-step-3");
  };

  return (
    <div
      className="flex flex-col min-h-screen text-left bg-white rounded-lg lg:border lg:p-5"
      data-testid="account-setup-step2">
      {/* Top section */}
      <div className="hidden md:block">
        <h1 className="mb-2 text-3xl font-bold text-left whitespace-nowrap text-primary">
          Organization Owner&apos;s Information
        </h1>
        <p className="mb-4 text-left text-small text-darkGrey">
          Fill in the organization owner&apos;s information to proceed. If you
          are the owner of the organization, just click the toggle below to skip
          some of the steps required below.
        </p>
      </div>

      <div className="flex flex-col justify-between rounded-lg lg:border lg:p-5">
        <div className="flex items-center mb-6">
          <Switch
            checked={isOwner}
            onChange={handleToggleChange}
            className="bg-gray-300"
            checkedChildren=""
            unCheckedChildren=""
            style={{
              backgroundColor: isOwner
                ? "var(--veegil-color-primary)"
                : undefined,
            }}
            data-testid="owner-toggle"
          />
          <span className="ml-2 text-black text-small">
            I am the owner of this organization
          </span>
        </div>

        <h2 className="mb-4 text-left text-black heading-medium">
          Basic Information
        </h2>
        <ProfilePicker
          currentImage={profilePreview}
          onImageChange={handleProfileImageChange}
          className="mb-6"
          bgColor="lightGray"
          isLoading={false}
          data-testid="owner-profile-picker"
        />

        <div className="mb-4">
          <Input
            label="FULL NAME"
            placeholder="Enter your full name"
            icon={<WebIcon icon="Profile" className="web-icon-large" />}
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            data-testid="owner-name-input"
          />
        </div>

        <div className="mb-4">
          <Input
            label="EMAIL"
            placeholder="email@domain.com"
            icon={<WebIcon icon="Message" className="web-icon-large" />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="owner-email-input"
          />
        </div>

        <div className="mb-4">
          <Input
            label="PHONE"
            placeholder="Phone Number"
            type="tel"
            icon={<CountryFlag />}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="owner-phone-input"
          />
        </div>

        <h2 className="mt-6 mb-4 text-left text-black heading-medium">
          Identity Information
        </h2>
        <p className="mb-4 text-left text-small text-darkGrey">
          Please provide you government-issued or a nationality recognized mode
          of identity to verify who you are.
        </p>

        <div className="mb-4">
          <Input
            label="MODE OF IDENTIFICATION"
            placeholder="Select"
            type="select"
            options={identityTypeOptions}
            value={identityType}
            onChange={(e) => setIdentityType(e.target.value)}
            data-testid="identification-mode-input"
          />
        </div>

        {identityType && (
          <div className="mb-4">
            <FileInput
              id="identityDocument"
              label="UPLOAD FILE"
              placeholder={identityDocument ? "File uploaded" : "File Name.png"}
              onChange={(file) => handleIdentityDocumentChange(file)}
              accept=".pdf,.jpg,.jpeg,.png"
              isLoading={isFileUploading}
            />
          </div>
        )}
        <Button
          text="Proceed"
          icon={<ArrowRightOutlined />}
          onClick={handleProceed}
          isActive={isFormFilled}
          color="darkBlue"
          className={`w-full mt-6 ${
            isFormFilled ? "bg-primary text-white" : "bg-darkBlue text-primary"
          }`}
          data-testid="step2-proceed-button"
        />
      </div>

      <div className="mt-auto">
        <Copyright />
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="KYC Step Saved"
        paragraph="Your kyc step has been saved. You can proceed below or save and continue later."
        buttonText="Proceed"
        showCancelButton={true}
        cancelButtonText="I want to save and proceed later"
        onButtonClick={handleFirstSuccessProceed}
        onCancelClick={handleSaveAndProceedLater}
      />

      <Success
        isVisible={isSecondSuccessVisible}
        onClose={handleCloseSecondSuccess}
        header="KYC Step Saved"
        paragraph="You can proceed with your KYC at any time when you are ready but remember, your account will still be limited until approval."
        buttonText="Continue"
        showCancelButton={false}
        onButtonClick={handleContinue}
      />
    </div>
  );
};

export default AccountSetupStep2;
